import { useState, useEffect, useCallback } from "react";

const MQTT_SERVERS = {
  ps2: { label:"TN-PS-MQTT/TCP", url:"/mqtt-ps2-api", accent:"#8b5cf6" },
  fsa: { label:"FSA",            url:"/fsa-mqtt-api",  accent:"#f59e0b" },
};

function fmt(n) {
  if (n >= 1000000) return `${(n/1000000).toFixed(1)}M`;
  if (n >= 1000)    return `${(n/1000).toFixed(1)}K`;
  return String(n);
}

function fmtUptime(sec) {
  if (!sec) return "—";
  if (sec > 86400) return `${Math.floor(sec/86400)}d ${Math.floor((sec%86400)/3600)}h`;
  if (sec > 3600)  return `${Math.floor(sec/3600)}h ${Math.floor((sec%3600)/60)}m`;
  return `${Math.floor(sec/60)}m`;
}

export default function MqttPanel({ token, theme, env }) {
  const currentEnv = env || (typeof sessionStorage!=="undefined"?sessionStorage.getItem("bn360_env"):null) || "tnpdcl";
  const visibleServers = Object.fromEntries(
    Object.entries(MQTT_SERVERS).filter(([k]) =>
      currentEnv==="fsa" ? k==="fsa" : k!=="fsa"
    )
  );
  const [server,  setServer]  = useState(currentEnv==="fsa"?"fsa":"ps2");
  useEffect(()=>{
    const e = env || sessionStorage.getItem("bn360_env") || "tnpdcl";
    setServer(e==="fsa"?"fsa":"ps2");
  },[env]);
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  const H   = { "X-Auth-Token": token };
  const activeKey = visibleServers[server] ? server : Object.keys(visibleServers)[0];
  const cfg = MQTT_SERVERS[activeKey] || Object.values(visibleServers)[0];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const curEnv = sessionStorage.getItem("bn360_env") || "tnpdcl";
      const url = curEnv==="fsa"
        ? "fsa-mqtt-api"
        : "mqtt-ps2-api";
      const r = await fetch(`${url}/mqtt`, { headers: {"X-Auth-Token": token} });
      setData(await r.json());
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); const id = setInterval(load, 30000); return ()=>clearInterval(id); }, [load, server]);

  const card = { background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:12, padding:"16px 20px" };

  const statCard = (label, value, color, sub, icon) => (
    <div style={{...card, textAlign:"center", minWidth:140}}>
      <div style={{fontSize:10,fontWeight:700,color:theme.textMuted,letterSpacing:".1em",
        fontFamily:"'Inter',sans-serif",marginBottom:8}}>{icon} {label}</div>
      <div style={{fontSize:26,fontWeight:800,color,fontFamily:"'Inter',sans-serif"}}>{value}</div>
      {sub&&<div style={{fontSize:11,color:theme.textMuted,fontFamily:"'Inter',sans-serif",marginTop:4}}>{sub}</div>}
    </div>
  );

  return (
    <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",minHeight:0,
      background:theme.bg,fontFamily:"'Inter',sans-serif"}}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Header */}
      <div style={{padding:"12px 20px",borderBottom:`1px solid ${theme.border}`,
        background:theme.bgCard,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap",flexShrink:0}}>
        <div style={{fontSize:16,fontWeight:800,color:theme.text}}>📡 MQTT Monitor</div>
        <div style={{width:1,height:20,background:theme.border}}/>
        {Object.entries(visibleServers).map(([k,s])=>(
          <button key={k} onClick={()=>setServer(k)} style={{
            background:server===k?`${s.accent}18`:theme.inputBg,
            border:`1px solid ${server===k?`${s.accent}50`:theme.border}`,
            color:server===k?s.accent:theme.textMuted,
            borderRadius:6,padding:"5px 12px",cursor:"pointer",
            fontFamily:"'Inter',sans-serif",fontSize:12,fontWeight:600,
          }}>{s.label}</button>
        ))}
        <div style={{flex:1}}/>
        <button onClick={load} style={{
          background:`${cfg.accent}18`,border:`1px solid ${cfg.accent}50`,
          color:cfg.accent,borderRadius:6,padding:"5px 12px",cursor:"pointer",
          fontFamily:"'Inter',sans-serif",fontSize:12,fontWeight:600,
        }}>↻ Refresh</button>
        {loading&&<div style={{width:16,height:16,borderRadius:"50%",
          border:`2px solid ${cfg.accent}30`,borderTopColor:cfg.accent,
          animation:"spin .7s linear infinite"}}/>}
      </div>

      {/* Content */}
      <div style={{flex:1,overflowY:"auto",padding:20,minHeight:0,paddingBottom:40}}>
        {!data && loading && (
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",
            flexDirection:"column",gap:16,height:300}}>
            <div style={{width:48,height:48,borderRadius:"50%",
              border:`3px solid ${cfg.accent}30`,borderTopColor:cfg.accent,
              animation:"spin .8s linear infinite"}}/>
            <div style={{fontSize:13,color:theme.textMuted}}>Loading MQTT metrics...</div>
          </div>
        )}

        {data && (
          <div style={{display:"flex",flexDirection:"column",gap:20}}>
            {data.map((broker,bi)=>(
              <div key={bi} style={{display:"flex",flexDirection:"column",gap:12}}>
                {/* Broker header */}
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <div style={{width:10,height:10,borderRadius:"50%",
                    background:broker.status==="online"?"#10b981":"#ef4444",
                    boxShadow:broker.status==="online"?"0 0 8px #10b981":"none"}}/>
                  <span style={{fontSize:15,fontWeight:700,color:theme.text,
                    fontFamily:"'Inter',sans-serif"}}>{broker.label}</span>
                  <span style={{fontSize:12,color:theme.textMuted,fontFamily:"'JetBrains Mono',monospace"}}>
                    :{broker.port}
                  </span>
                  <span style={{fontSize:11,padding:"2px 10px",borderRadius:20,
                    background:broker.status==="online"?"rgba(16,185,129,0.15)":"rgba(239,68,68,0.15)",
                    color:broker.status==="online"?"#10b981":"#ef4444",fontWeight:700}}>
                    {broker.status.toUpperCase()}
                  </span>
                  <span style={{fontSize:11,color:theme.textMuted,marginLeft:4}}>
                    uptime: {fmtUptime(broker.uptime_seconds)}
                  </span>
                </div>

                {/* Stat cards */}
                <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
                  {statCard("CONNECTED CLIENTS", fmt(broker.clients?.connected||0), cfg.accent,
                    `max ever: ${fmt(broker.clients?.maximum||0)}`, "🔌")}
                  {statCard("TOTAL CLIENTS", fmt(broker.clients?.total||0), "#06b6d4",
                    "all time", "👥")}
                  {statCard("MSGS RECEIVED", fmt(broker.messages?.received||0), "#10b981",
                    "total messages", "📥")}
                  {statCard("MSGS SENT", fmt(broker.messages?.sent||0), "#f59e0b",
                    "total messages", "📤")}
                  {statCard("MSGS STORED", fmt(broker.messages?.stored||0), "#a78bfa",
                    "in queue", "📦")}
                  {statCard("SUBSCRIPTIONS", fmt(broker.subscriptions||0), "#e11d48",
                    "active subs", "📋")}
                </div>

                {/* Message rates */}
                <div style={card}>
                  <div style={{fontSize:12,fontWeight:700,color:theme.textMuted,
                    letterSpacing:".1em",marginBottom:14}}>📊 MESSAGE THROUGHPUT</div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
                    {[
                      ["Msgs Received/sec", broker.messages?.per_sec_recv||0, "#10b981"],
                      ["Msgs Sent/sec",     broker.messages?.per_sec_sent||0, "#f59e0b"],
                      ["Connected Clients", broker.clients?.connected||0,     cfg.accent],
                    ].map(([label,val,color])=>(
                      <div key={label} style={{background:theme.inputBg,borderRadius:8,
                        padding:"12px 16px",textAlign:"center"}}>
                        <div style={{fontSize:10,color:theme.textMuted,letterSpacing:".08em",
                          marginBottom:6,fontFamily:"'Inter',sans-serif"}}>{label}</div>
                        <div style={{fontSize:20,fontWeight:700,color,
                          fontFamily:"'JetBrains Mono',monospace"}}>{val}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Broker info */}
                <div style={card}>
                  <div style={{fontSize:12,fontWeight:700,color:theme.textMuted,
                    letterSpacing:".1em",marginBottom:12}}>🖥 BROKER INFO</div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                    {[
                      ["Port",        broker.port],
                      ["Version",     broker.version||"2.0.18"],
                      ["Uptime",      fmtUptime(broker.uptime_seconds)],
                      ["Log File",    broker.log_file||"—"],
                      ["Log Size",    broker.log_size||"—"],
                      ["Active Conns",broker.clients?.active_connections||0],
                    ].map(([k,v])=>(
                      <div key={k} style={{display:"flex",justifyContent:"space-between",
                        padding:"6px 10px",borderRadius:6,background:theme.inputBg}}>
                        <span style={{fontSize:12,color:theme.textMuted}}>{k}</span>
                        <span style={{fontSize:12,fontWeight:600,color:theme.text,
                          fontFamily:"'JetBrains Mono',monospace",
                          overflow:"hidden",textOverflow:"ellipsis",maxWidth:180}}>{String(v)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Connections by IP */}
                {(broker.connections_by_ip||[]).length > 0 && (
                  <div style={card}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
                      <div style={{fontSize:12,fontWeight:700,color:theme.textMuted,letterSpacing:".1em"}}>
                        🌐 CONNECTED DEVICES BY IP
                      </div>
                      <div style={{fontSize:11,color:theme.textMuted,fontStyle:"italic"}}>
                        IoT meter devices · {(broker.connections_by_ip||[]).reduce((a,c)=>a+c.count,0)} total connections
                      </div>
                    </div>
                    {/* Summary stats */}
                    <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:12}}>
                      {[
                        ["Total IPs", (broker.connections_by_ip||[]).length, cfg.accent],
                        ["Local/NAT (1.1.1.1)", (broker.connections_by_ip||[]).find(x=>x.ip==="1.1.1.1")?.count||0, "#06b6d4"],
                        ["External Devices", (broker.connections_by_ip||[]).filter(x=>x.ip!=="1.1.1.1").reduce((a,c)=>a+c.count,0), "#10b981"],
                      ].map(([label,val,color])=>(
                        <div key={label} style={{background:theme.inputBg,borderRadius:8,
                          padding:"10px 14px",textAlign:"center"}}>
                          <div style={{fontSize:10,color:theme.textMuted,marginBottom:4,
                            fontFamily:"'Inter',sans-serif",letterSpacing:".06em"}}>{label}</div>
                          <div style={{fontSize:18,fontWeight:700,color,
                            fontFamily:"'JetBrains Mono',monospace"}}>{val}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:3,maxHeight:300,overflowY:"auto"}}>
                      {(broker.connections_by_ip||[]).map((c,i)=>(
                        <div key={i} style={{display:"flex",justifyContent:"space-between",
                          alignItems:"center",padding:"6px 10px",borderRadius:6,
                          background:i%2===0?theme.inputBg:"transparent"}}>
                          <div style={{display:"flex",alignItems:"center",gap:8}}>
                            <div style={{width:6,height:6,borderRadius:"50%",flexShrink:0,
                              background:c.ip==="1.1.1.1"?"#06b6d4":cfg.accent}}/>
                            <span style={{fontSize:12,color:theme.text,
                              fontFamily:"'JetBrains Mono',monospace"}}>{c.ip}</span>
                            {c.ip==="1.1.1.1" && (
                              <span style={{fontSize:10,color:"#06b6d4",background:"rgba(6,182,212,0.1)",
                                padding:"1px 6px",borderRadius:4}}>local/NAT</span>
                            )}
                          </div>
                          <div style={{display:"flex",alignItems:"center",gap:8}}>
                            <div style={{width:60,background:theme.border,borderRadius:3,height:4}}>
                              <div style={{width:`${Math.min(c.count/((broker.connections_by_ip||[])[0]?.count||1)*100,100)}%`,
                                height:"100%",borderRadius:3,background:c.ip==="1.1.1.1"?"#06b6d4":cfg.accent}}/>
                            </div>
                            <span style={{fontSize:12,fontWeight:700,color:cfg.accent,
                              fontFamily:"'JetBrains Mono',monospace",minWidth:55,textAlign:"right"}}>
                              {c.count} conn
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {bi < data.length-1 && (
                  <div style={{height:1,background:theme.border,margin:"8px 0"}}/>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
